let ws = null;
let onNotificationCallback = null;
let reconnectAttempts = 0;
const maxReconnectAttempts = 5;
let lastWsErrorLogAt = 0;
const WS_ERROR_LOG_COOLDOWN_MS = 15000;
let userId = null;
let userRole = null;
let reconnectTimeout = null;
let isReconnecting = false;
let isConnecting = false; // Thêm flag để tránh multiple connections

const hasActiveSession = () => {
  try {
    const storedUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!storedUser || !authToken) return false;

    const parsedUser = JSON.parse(storedUser);
    return Boolean(parsedUser && (parsedUser.id || parsedUser._id || parsedUser.username));
  } catch (error) {
    return false;
  }
};

// Danh sách ID thông báo đã nhận để tránh trùng lặp - sử dụng LRU cache
const MAX_CACHE_SIZE = 100;
let receivedNotificationIds = new Set();

// Thêm hàm để giới hạn kích thước cache
const limitCacheSize = () => {
  if (receivedNotificationIds.size > MAX_CACHE_SIZE) {
    const idsArray = Array.from(receivedNotificationIds);
    receivedNotificationIds = new Set(idsArray.slice(-Math.floor(MAX_CACHE_SIZE / 2))); // Giữ lại một nửa gần đây nhất
  }
};

// Lấy WebSocket URL dựa trên môi trường
const getWebSocketURL = () => {
  try {
    const isProduction = window.location.protocol === 'https:';
    const host = window.location.hostname;
    const port = window.location.port;
    
    // Log để debug
    console.log('[WebSocket] Environment:', { 
      protocol: window.location.protocol,
      host,
      port,
      isProduction
    });
    
    // Xác định loại môi trường
    const isRailway = host.includes('railway.app') || host.includes('up.railway.app');
    const isNewAtlantic = host.includes('newatlantic.vn');
    const isCustomDomain = !host.includes('localhost') && !host.includes('127.0.0.1');
    
    // Xử lý các trường hợp khác nhau
    if (isProduction || isRailway || isNewAtlantic || (isCustomDomain && window.location.protocol === 'https:')) {
      // Trong môi trường production với HTTPS
      console.log('[WebSocket] Using secure WebSocket connection (wss://)');
      // Add a timestamp to prevent caching issues
      return `wss://${host}/ws?t=${Date.now()}`;
    } else if (isCustomDomain) {
      // Server khác localhost nhưng không phải HTTPS
      console.log('[WebSocket] Using non-secure WebSocket connection for custom domain (ws://)');
      return `ws://${host}${port ? ':' + port : ''}/ws?t=${Date.now()}`;
    }
    
    // For development - LƯU Ý PORT PHẢI ĐÚNG
    console.log('[WebSocket] Using localhost development connection');
    return `ws://localhost:5000/ws?t=${Date.now()}`;
  } catch (error) {
    console.error('[WebSocket] Error generating WebSocket URL:', error);
    return null;
  }
};

/**
 * Kết nối WebSocket thuần
 * @param {{ userId: string, role: string }} info 
 * @param {function} callback  Hàm gọi mỗi khi nhận { type: 'notification' }
 */
export function connectWebSocket(info, callback) {
  if (!hasActiveSession()) {
    console.log('[WebSocket] No active session, skip connect');
    return null;
  }

  if (!info || !info.userId) {
    console.warn('[WebSocket] Missing userId, skip connect');
    return null;
  }

  // Tránh tạo multiple connections
  if (isConnecting) {
    console.log('[WebSocket] Already connecting, skipping...');
    return ws;
  }

  const normalizedRole = String(info.role || '')
    .replace(/_ROOM$/, '')
    .trim();

  // Store user info for reconnection
  userId = info.userId;
  userRole = normalizedRole;
  onNotificationCallback = callback;

  // Đóng kết nối cũ nếu có
  if (ws && ws.readyState !== WebSocket.CLOSED) {
    console.log('[WebSocket] Closing existing connection');
    ws.onclose = null; // Tránh trigger reconnect
    ws.close();
    ws = null;
  }

  // Get appropriate WebSocket URL
  const wsUrl = getWebSocketURL();
  if (!wsUrl) {
    console.error('[WebSocket] Failed to get WebSocket URL, cannot connect');
    return null;
  }
  
  try {
    isConnecting = true;
    console.log('[WebSocket] Attempting to connect to:', wsUrl);
    
    // Timeout để tránh kết nối bị treo
    const connectionTimeout = setTimeout(() => {
      console.error('[WebSocket] Connection attempt timed out');
      isConnecting = false;
      if (ws && ws.readyState !== WebSocket.OPEN) {
        ws.close();
        attemptReconnect();
      }
    }, 10000);
    
    ws = new WebSocket(wsUrl);
    
    // Xóa timeout khi kết nối thành công hoặc thất bại
    const clearConnectionTimeout = () => {
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }
    };

    ws.onopen = () => {
      clearConnectionTimeout();
      isConnecting = false;
      
      console.log('[WebSocket] Connected successfully to', wsUrl);
      reconnectAttempts = 0;
      
      // Đặt biến global để theo dõi trạng thái kết nối
      window.wsConnected = true;
      
      // Gửi join => { type: 'join', userId, role: 'EMPLOYEE_ROOM' ... }
      try {
        ws.send(JSON.stringify({
          type: 'join',
          userId: info.userId,
          role: `${normalizedRole || 'GENERAL'}_ROOM`
        }));
        console.log('[WebSocket] Join message sent successfully');
      } catch (error) {
        console.error('[WebSocket] Error sending join message:', error);
      }
      
      // Kích hoạt sự kiện để thông báo các component rằng WebSocket đã kết nối
      try {
        const wsConnectedEvent = new CustomEvent('ws_connected', {
          detail: { timestamp: Date.now() }
        });
        window.dispatchEvent(wsConnectedEvent);
        console.log('[WebSocket] Dispatched ws_connected event');
      } catch (eventError) {
        console.error('[WebSocket] Error dispatching ws_connected event:', eventError);
      }
    };
    
    ws.onmessage = (event) => {
      try {
        console.log('[WebSocket] Message received:', event.data);
        let data;
        
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          console.error('[WebSocket] Failed to parse message as JSON:', parseError);
          console.log('[WebSocket] Raw message:', event.data);
          return;
        }
        
        // Kiểm tra xem data có phải là thông báo không
        if (!data) {
          console.log('[WebSocket] Received empty message');
          return;
        }
        
        // Xử lý các loại thông báo khác nhau
        const validTypes = [
          'notification', 
          'driver_assigned', 
          'driver_schedule_cancelled',
          'driver_schedule_deleted',
          'driver_schedule_rejected',
          'request_cancelled', 
          'request_rejected', 
          'request_approved',
          'system_status_change',
          'system_notification'
        ];
        const isNotification = validTypes.includes(data.type) || data.message;
        
        if (!isNotification) {
          console.log('[WebSocket] Received non-notification message:', data);
          return;
        }
        
        console.log('[WebSocket] Notification received:', data);
        
        // Kiểm tra thông báo đã nhận chưa để tránh trùng lặp
        const notificationId = data._id || `${Date.now()}-${Math.random()}`;
        
        // Nếu thông báo đã được xử lý trước đó, bỏ qua
        if (receivedNotificationIds.has(notificationId)) {
          console.log('[WebSocket] Duplicate notification, ignoring:', notificationId);
          return;
        }
        
        // Đánh dấu thông báo đã được xử lý
        receivedNotificationIds.add(notificationId);
        limitCacheSize(); // Đảm bảo kích thước cache không quá lớn
        
        // Xử lý đặc biệt cho các loại thông báo liên quan đến tài xế
        const driverNotificationTypes = [
          'driver_assigned',
          'driver_schedule_cancelled',
          'driver_schedule_deleted',
          'driver_schedule_rejected'
        ];
        
        // Xử lý đặc biệt cho thông báo thay đổi trạng thái hệ thống
        if (data.type === 'system_status_change') {
          console.log('[WebSocket] Received system status change notification:', data);
          
          // Kích hoạt sự kiện để thông báo các component về thay đổi trạng thái hệ thống
          try {
            // Extract the data correctly - handle both formats
            const statusData = data.data || data;
            
            console.log('[WebSocket] System status change data:', statusData);
            
            // Create and dispatch the event
            const systemStatusEvent = new CustomEvent('system_status_change', {
              detail: {
                isActive: statusData.isActive,
                maintenanceMessage: statusData.maintenanceMessage,
                timestamp: statusData.timestamp || Date.now()
              }
            });
            
            window.dispatchEvent(systemStatusEvent);
            console.log('[WebSocket] Dispatched system_status_change event');
            
            // Force reload after a delay to ensure all users get the update
            // This is a backup in case the SystemStatusContext handler doesn't work
            setTimeout(() => {
              console.log('[WebSocket] Forcing page reload due to system status change');
              window.location.reload();
            }, 3500);
            
            return; // Không cần xử lý tiếp theo
          } catch (eventError) {
            console.error('[WebSocket] Error dispatching system_status_change event:', eventError);
            console.error('Error details:', eventError.message);
            
            // Even if there's an error, try to reload the page
            setTimeout(() => {
              console.log('[WebSocket] Forcing page reload after error');
              window.location.reload();
            }, 4000);
          }
        }
        
        // Nếu là thông báo liên quan đến tài xế, thêm thông tin bổ sung
        if (driverNotificationTypes.includes(data.type)) {
          console.log(`[WebSocket] Received driver notification: ${data.type}`);
          
          // Kích hoạt sự kiện đặc biệt cho thông báo tài xế
          try {
            const driverEvent = new CustomEvent('driver_notification', {
              detail: {
                type: data.type,
                data: data,
                timestamp: Date.now()
              }
            });
            window.dispatchEvent(driverEvent);
            console.log('[WebSocket] Dispatched driver_notification event');
          } catch (eventError) {
            console.error('[WebSocket] Error dispatching driver_notification event:', eventError);
          }
        }
        
        // Gọi callback
        if (typeof onNotificationCallback === 'function') {
          try {
            onNotificationCallback(data);
          } catch (callbackError) {
            console.error('[WebSocket] Error in notification callback:', callbackError);
          }
        }
        
        // Kích hoạt sự kiện window để NotificationBell lắng nghe
        console.log('[WebSocket] Dispatching notification_received event with data:', data);
        
        // Tính toán unreadCount - nếu thông báo chưa đọc, tăng thêm 1
        let calculatedUnreadCount = null;
        if (data.isRead === false) {
          // Lấy giá trị hiện tại từ DOM nếu có
          const countElement = document.querySelector('.notification-count');
          const currentCount = countElement ? parseInt(countElement.textContent, 10) : 0;
          calculatedUnreadCount = isNaN(currentCount) ? 1 : currentCount + 1;
          console.log('[WebSocket] Calculated unread count:', calculatedUnreadCount);
        }
        
        try {
          const customEvent = new CustomEvent('notification_received', {
            detail: { 
              notification: data,
              unreadCount: data.unreadCount || calculatedUnreadCount, // Ưu tiên unreadCount từ server
              timestamp: Date.now() // Thêm timestamp để giúp phát hiện trùng lặp
            }
          });
          window.dispatchEvent(customEvent);
          console.log('[WebSocket] Successfully dispatched notification event');
        } catch (eventError) {
          console.error('[WebSocket] Error dispatching notification event:', eventError);
        }
      } catch (err) {
        console.error('[WebSocket] Error handling message:', err);
      }
    };
    
    ws.onclose = (event) => {
      clearConnectionTimeout();
      isConnecting = false;
      
      console.log('[WebSocket] Connection closed', event.code, event.reason);
      window.wsConnected = false;
      
      // Kích hoạt sự kiện để thông báo các component rằng WebSocket đã đóng
      try {
        const wsDisconnectedEvent = new CustomEvent('ws_disconnected', {
          detail: { 
            code: event.code,
            reason: event.reason,
            timestamp: Date.now() 
          }
        });
        window.dispatchEvent(wsDisconnectedEvent);
        console.log('[WebSocket] Dispatched ws_disconnected event');
      } catch (eventError) {
        console.error('[WebSocket] Error dispatching ws_disconnected event:', eventError);
      }
      
      // Only reconnect if not a normal closure and user is still authenticated
      if (event.code !== 1000 && event.code !== 1001 && hasActiveSession()) {
        attemptReconnect();
      }
    };

    ws.onerror = (err) => {
      clearConnectionTimeout();
      isConnecting = false;

      const now = Date.now();
      if (now - lastWsErrorLogAt >= WS_ERROR_LOG_COOLDOWN_MS) {
        lastWsErrorLogAt = now;
        console.error('[WebSocket] Connection error:', err);

        // Log additional information for debugging
        console.log('[WebSocket] Error details:', {
          readyState: ws ? ws.readyState : 'No WebSocket',
          url: wsUrl,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        });
      }

      window.wsConnected = false;
      
      // Kích hoạt sự kiện để thông báo các component rằng WebSocket gặp lỗi
      try {
        const wsErrorEvent = new CustomEvent('ws_error', {
          detail: { 
            error: err,
            timestamp: Date.now() 
          }
        });
        window.dispatchEvent(wsErrorEvent);
        console.log('[WebSocket] Dispatched ws_error event');
        
        // Attempt to reconnect after error
        setTimeout(() => {
          if (hasActiveSession()) {
            attemptReconnect();
          }
        }, 2000);
      } catch (eventError) {
        console.error('[WebSocket] Error dispatching ws_error event:', eventError);
      }
    };
    
    return ws;
  } catch (error) {
    isConnecting = false;
    console.error('Failed to connect WebSocket:', error);
    attemptReconnect();
    return null;
  }
}

// Function to handle reconnection with exponential backoff
function attemptReconnect() {
  // Kiểm tra điều kiện để tránh reconnect không cần thiết
  if (reconnectAttempts >= maxReconnectAttempts) {
    console.log('[WebSocket] Max reconnection attempts reached, giving up');
    
    // Thông báo cho người dùng
    try {
      const wsMaxAttemptsEvent = new CustomEvent('ws_max_attempts', {
        detail: { timestamp: Date.now() }
      });
      window.dispatchEvent(wsMaxAttemptsEvent);
    } catch (eventError) {
      console.error('[WebSocket] Error dispatching ws_max_attempts event:', eventError);
    }
    
    return;
  }
  
  if (isReconnecting || isConnecting) {
    console.log('[WebSocket] Already attempting to reconnect, skipping');
    return;
  }
  
  // Kiểm tra xem người dùng có đăng nhập không
  const hasSession = hasActiveSession();
  if (!hasSession) {
    console.log('[WebSocket] No active session, skipping reconnect');
    return;
  }
  
  isReconnecting = true;
  
  // Tính toán thời gian chờ với jitter để tránh thundering herd
  const baseDelay = Math.min(Math.pow(2, reconnectAttempts) * 1000, 30000); // Tối đa 30 giây
  const jitter = Math.random() * 1000; // Thêm jitter ngẫu nhiên từ 0-1000ms
  const delay = baseDelay + jitter;
  
  console.log(`[WebSocket] Attempting to reconnect in ${Math.round(delay)}ms... (Attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
  
  // Thông báo cho người dùng về việc đang thử kết nối lại
  try {
    const wsReconnectingEvent = new CustomEvent('ws_reconnecting', {
      detail: { 
        attempt: reconnectAttempts + 1,
        maxAttempts: maxReconnectAttempts,
        delay: Math.round(delay),
        timestamp: Date.now() 
      }
    });
    window.dispatchEvent(wsReconnectingEvent);
  } catch (eventError) {
    console.error('[WebSocket] Error dispatching ws_reconnecting event:', eventError);
  }
  
  // Xóa timeout cũ nếu có
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
  }
  
  reconnectTimeout = setTimeout(() => {
    reconnectAttempts++;
    isReconnecting = false;
    
    if (userId && userRole) {
      try {
        connectWebSocket({ userId, role: userRole }, onNotificationCallback);
      } catch (error) {
        console.error('[WebSocket] Error during reconnect:', error);
        // Nếu lỗi, thử lại
        isReconnecting = false;
        attemptReconnect();
      }
    } else {
      console.warn('[WebSocket] Missing userId or userRole for reconnect');
      isReconnecting = false;
    }
  }, delay);
}

export function closeWebSocket() {
  // Clear all timeouts
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  
  // Reset flags
  isReconnecting = false;
  isConnecting = false;
  reconnectAttempts = 0;
  
  if (ws) {
    try {
      ws.onclose = null; // Prevent triggering reconnect
      ws.onerror = null;
      ws.onmessage = null;
      ws.onopen = null;
      ws.close(1000, "Closed by user");
    } catch (err) {
      console.error("Error closing WebSocket:", err);
    }
    ws = null;
  }
  
  // Clear notification cache
  receivedNotificationIds.clear();
  
  // Reset global variables
  window.wsConnected = false;
  userId = null;
  userRole = null;
  onNotificationCallback = null;
}
